/**
 * Admin Incoming Requests Inbox (Phase 3.3).
 *
 * Three tabs reflect the three lead types from /catalogue:
 *    Partner Brands  | Any Brands  | Consultations
 *
 * Each row expands into a side panel where the admin can:
 *  • see customer details + the products they asked for
 *  • click "Suggest prices (AI)" to autofill GEL prices from Gemini
 *  • edit each line, add delivery fee / admin note, hit "Send quote"
 *  • copy the WhatsApp deep-link the backend returns for manual send
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Sparkles,
  Send,
  ExternalLink,
  Phone,
  Mail,
  Inbox,
  Loader2,
  ClipboardCopy,
  CheckCircle2,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { getToken } from '@/lib/adminApi';

const API = process.env.REACT_APP_BACKEND_URL || '';

const TYPES = [
  { key: 'partner_brand', label: 'Partner Brands' },
  { key: 'any_brand',     label: 'Any Brands' },
  { key: 'consultation',  label: 'Consultations' },
];

const STATUS_STYLES = {
  new:      'bg-[#EAC490]/60 text-[#05223D]',
  quoted:   'bg-[#0A4D8C]/12 text-[#0A4D8C]',
  accepted: 'bg-[#A9B89A]/55 text-[#05223D]',
  rejected: 'bg-[#9b1c1c]/15 text-[#9b1c1c]',
  closed:   'bg-[#465B70]/15 text-[#465B70]',
};

async function adminFetch(path, init = {}) {
  const token = getToken();
  const res = await fetch(`${API}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init.headers || {}),
      Authorization: token ? `Bearer ${token}` : '',
    },
  });
  if (!res.ok) {
    const e = await res.json().catch(() => ({}));
    throw new Error(e.detail || `${res.status}`);
  }
  if (res.status === 204) return null;
  return res.json();
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
}

export default function AdminRequestsInbox() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialType = TYPES.find((t) => t.key === searchParams.get('type'))?.key || 'partner_brand';
  const initialId = searchParams.get('id') || null;
  const [activeTab, setActiveTab] = useState(initialType);
  const [data, setData] = useState({ items: [], counts: {} });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [deletingRow, setDeletingRow] = useState(null);
  const [deleteWorking, setDeleteWorking] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await adminFetch(`/api/admin/requests/inbox?request_type=${activeTab}`);
      setData(d);
      // Auto-open the row matching ?id=<rid> (from email deep-link)
      if (initialId && !selected) {
        const m = d.items.find((it) => it.id === initialId);
        if (m) setSelected(m);
      }
    } catch (e) {
      console.error('inbox load failed', e);
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteRequest = async () => {
    if (!deletingRow) return;
    setDeleteWorking(true);
    try {
      await adminFetch(`/api/admin/requests/${deletingRow.id}`, { method: 'DELETE' });
      toast.success(`Request from ${deletingRow.name || deletingRow.email || deletingRow.phone} deleted`);
      setDeletingRow(null);
      // If currently open in the side panel, close it
      if (selected && selected.id === deletingRow.id) closeDetail();
      await load();
    } catch (e) {
      toast.error(`Delete failed: ${e.message}`);
    } finally {
      setDeleteWorking(false);
    }
  };

  useEffect(() => { load(); }, [activeTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const counts = data.counts || {};

  const openDetail = (row) => {
    setSelected(row);
    const next = new URLSearchParams(searchParams);
    next.set('id', row.id);
    next.set('type', activeTab);
    setSearchParams(next, { replace: true });
  };

  const closeDetail = () => {
    setSelected(null);
    const next = new URLSearchParams(searchParams);
    next.delete('id');
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-5" data-testid="admin-requests-inbox">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-[#05223D]">Incoming Requests</h1>
          <p className="text-sm text-[#465B70] mt-1">
            New customer requests from the catalogue lead-capture flow.
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(v) => { setActiveTab(v); setSelected(null); }}>
        <TabsList className="grid w-full grid-cols-3 sm:w-auto" data-testid="inbox-tabs">
          {TYPES.map((t) => (
            <TabsTrigger key={t.key} value={t.key} data-testid={`inbox-tab-${t.key}`}>
              {t.label}
              {counts[t.key] > 0 && (
                <Badge variant="secondary" className="ml-2 rounded-full">
                  {counts[t.key]}
                </Badge>
              )}
            </TabsTrigger>
          ))}
        </TabsList>

        {TYPES.map((t) => (
          <TabsContent key={t.key} value={t.key} className="mt-4">
            <Card>
              <CardContent className="p-0">
                {loading ? (
                  <div className="flex items-center justify-center py-16 text-[#465B70]">
                    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
                  </div>
                ) : data.items.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-[#465B70]">
                    <Inbox className="h-8 w-8 mb-2" />
                    No {t.label.toLowerCase()} requests yet.
                  </div>
                ) : (
                  <ul className="divide-y">
                    {data.items.map((row) => (
                      <li
                        key={row.id}
                        className="grid grid-cols-[1fr_auto_auto_auto] items-center gap-3 px-4 py-3 hover:bg-[#FDFBF7]"
                        data-testid={`inbox-row-${row.id}`}
                      >
                        <div
                          className="min-w-0 cursor-pointer"
                          onClick={() => openDetail(row)}
                        >
                          <p className="font-bold text-[#05223D] truncate">{row.name || row.email || row.phone}</p>
                          <p className="text-xs text-[#465B70] truncate">
                            {row.email && <><Mail className="inline h-3 w-3 mr-1" />{row.email} · </>}
                            <Phone className="inline h-3 w-3 mr-1" />{row.phone}
                          </p>
                          <p className="text-xs text-[#465B70] mt-0.5">
                            {row.type === 'consultation'
                              ? <>📅 {row.preferred_time || '—'}</>
                              : <>{(row.items || []).length} product{(row.items || []).length === 1 ? '' : 's'} requested</>}
                            {' · '}
                            {fmtDate(row.created_at)}
                          </p>
                        </div>
                        <Badge className={`rounded-full ${STATUS_STYLES[row.status] || ''}`}>
                          {row.status}
                        </Badge>
                        <button
                          type="button"
                          onClick={() => openDetail(row)}
                          className="text-[#0A4D8C] hover:text-[#053B70] p-1.5 rounded-full hover:bg-[#0A4D8C0F]"
                          title="View details"
                          data-testid={`inbox-view-${row.id}`}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeletingRow(row);
                          }}
                          className="text-red-600 hover:text-red-800 p-1.5 rounded-full hover:bg-red-50"
                          title="Delete request"
                          data-testid={`inbox-delete-${row.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}
      </Tabs>

      <Sheet open={!!selected} onOpenChange={(o) => !o && closeDetail()}>
        <SheetContent
          className="w-full sm:max-w-2xl overflow-y-auto p-0"
          data-testid="inbox-detail-panel"
        >
          {selected && (
            <RequestDetail
              request={selected}
              onUpdated={(updated) => {
                setSelected(updated);
                load();
              }}
            />
          )}
        </SheetContent>
      </Sheet>

      {/* Delete-request confirmation */}
      <AlertDialog
        open={!!deletingRow}
        onOpenChange={(o) => !o && !deleteWorking && setDeletingRow(null)}
      >
        <AlertDialogContent className="bg-paper" data-testid="delete-request-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl">
              Delete this request?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove the request from{' '}
              <strong className="text-[#05223D]">
                {deletingRow?.name || deletingRow?.email || deletingRow?.phone || '—'}
              </strong>
              . This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteWorking}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteRequest}
              disabled={deleteWorking}
              data-testid="delete-request-confirm-btn"
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {deleteWorking ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
              ) : (
                <Trash2 className="h-4 w-4 mr-1.5" />
              )}
              Delete permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function RequestDetail({ request, onUpdated }) {
  const isConsult = request.type === 'consultation';
  const [lines, setLines] = useState(
    (request.items || []).map((it) => ({
      product_name: it.product_name || '',
      unit: it.unit || 'kg',
      quantity: it.quantity || 1,
      unit_price: 0,
      line_total: 0,
      note: it.comment || '',
    })),
  );
  const [deliveryFee, setDeliveryFee] = useState(0);
  const [adminNote, setAdminNote] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null); // { quote_url, whatsapp_url, total }
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');

  const subtotal = useMemo(
    () => lines.reduce((s, l) => s + (Number(l.line_total) || 0), 0),
    [lines],
  );
  const grandTotal = subtotal + (Number(deliveryFee) || 0);

  const updateLine = (i, patch) => {
    setLines((cur) =>
      cur.map((l, idx) => {
        if (idx !== i) return l;
        const next = { ...l, ...patch };
        if (patch.unit_price !== undefined || patch.quantity !== undefined) {
          next.line_total = Number(((Number(next.unit_price) || 0) * (Number(next.quantity) || 0)).toFixed(2));
        }
        return next;
      }),
    );
  };

  const runAI = async () => {
    setAiBusy(true);
    setError('');
    try {
      const body = {
        items: (request.items || []).map((it) => ({
          product_name: it.product_name,
          unit: it.unit,
          quantity: it.quantity,
          frequency: it.frequency || 'monthly',
        })),
      };
      const r = await adminFetch(`/api/admin/requests/${request.id}/ai-pricing`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      // Map suggestions to lines. Suggestion is the LINE TOTAL (not per-unit).
      const suggestions = r.suggestions || [];
      setLines((cur) =>
        cur.map((l, i) => {
          const s = suggestions.find((x) => x.index === i);
          if (!s) return l;
          const total = Number(s.price) || 0;
          const qty = Number(l.quantity) || 1;
          return {
            ...l,
            unit_price: Number((total / qty).toFixed(2)),
            line_total: Number(total.toFixed(2)),
          };
        }),
      );
    } catch (e) {
      setError(`AI pricing failed: ${e.message}`);
    } finally {
      setAiBusy(false);
    }
  };

  const sendQuote = async () => {
    setSending(true);
    setError('');
    setResult(null);
    try {
      const r = await adminFetch(`/api/admin/requests/${request.id}/quote`, {
        method: 'POST',
        body: JSON.stringify({
          lines,
          delivery_fee: Number(deliveryFee) || 0,
          currency: 'GEL',
          admin_note: adminNote || null,
        }),
      });
      setResult(r);
      onUpdated?.({ ...request, status: 'quoted' });
    } catch (e) {
      setError(`Send quote failed: ${e.message}`);
    } finally {
      setSending(false);
    }
  };

  const copyToClipboard = async (text, kind) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(kind);
      setTimeout(() => setCopied(''), 1500);
    } catch { /* noop */ }
  };

  return (
    <div data-testid={`inbox-detail-${request.id}`}>
      <SheetHeader className="p-6 pb-4 border-b">
        <SheetTitle className="font-display text-2xl flex items-center gap-3">
          {request.name || '—'}
          <Badge className={`rounded-full ${STATUS_STYLES[request.status] || ''}`}>{request.status}</Badge>
        </SheetTitle>
        <SheetDescription>
          {request.email || '—'} · {request.phone} · {fmtDate(request.created_at)}
        </SheetDescription>
      </SheetHeader>

      <div className="p-6 space-y-5">
        {isConsult ? (
          <>
            <div>
              <Label className="text-xs uppercase tracking-wider text-[#465B70]">Preferred time</Label>
              <p className="mt-1 font-bold text-[#05223D]">{request.preferred_time || '—'}</p>
            </div>
            {request.notes && (
              <div>
                <Label className="text-xs uppercase tracking-wider text-[#465B70]">Notes</Label>
                <p className="mt-1 whitespace-pre-wrap text-[#05223D]">{request.notes}</p>
              </div>
            )}
            <div className="rounded-xl bg-[#F5F2EB] p-3 text-sm text-[#465B70]">
              Consultation requests are handled by phone — no quote needed.
              Open WhatsApp:&nbsp;
              <a
                className="text-[#0A4D8C] font-bold underline"
                target="_blank" rel="noreferrer"
                href={`https://wa.me/${(request.phone || '').replace(/[^\d]/g, '')}`}
                data-testid="consultation-wa-link"
              >
                wa.me/{(request.phone || '').replace(/[^\d]/g, '')}
              </a>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-[#05223D]">Quote builder</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={runAI}
                disabled={aiBusy}
                className="rounded-xl"
                data-testid="ai-suggest-button"
              >
                {aiBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Sparkles className="mr-1.5 h-4 w-4" />}
                Suggest prices (AI)
              </Button>
            </div>

            <ul className="space-y-3">
              {lines.map((l, i) => (
                <li key={i} className="rounded-2xl border border-dashed border-[#0A4D8C]/15 p-3" data-testid={`quote-builder-line-${i}`}>
                  <p className="font-bold text-[#05223D] text-sm">{l.product_name}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 items-end">
                    <div>
                      <Label className="text-[10px] uppercase">Qty</Label>
                      <Input
                        type="number" min="0" step="0.1"
                        value={l.quantity}
                        onChange={(e) => updateLine(i, { quantity: Number(e.target.value) || 0 })}
                        className="h-9 rounded-lg tabular-nums"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase">Unit</Label>
                      <Input
                        value={l.unit}
                        onChange={(e) => updateLine(i, { unit: e.target.value })}
                        className="h-9 rounded-lg"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase">Unit price</Label>
                      <Input
                        type="number" min="0" step="0.01"
                        value={l.unit_price}
                        onChange={(e) => updateLine(i, { unit_price: Number(e.target.value) || 0 })}
                        data-testid={`quote-line-${i}-unit-price`}
                        className="h-9 rounded-lg tabular-nums"
                      />
                    </div>
                    <div>
                      <Label className="text-[10px] uppercase">Line total</Label>
                      <Input
                        type="number" min="0" step="0.01"
                        value={l.line_total}
                        onChange={(e) => updateLine(i, { line_total: Number(e.target.value) || 0 })}
                        data-testid={`quote-line-${i}-line-total`}
                        className="h-9 rounded-lg tabular-nums font-bold"
                      />
                    </div>
                  </div>
                  {l.note && <p className="text-xs italic text-[#465B70] mt-2">{l.note}</p>}
                </li>
              ))}
            </ul>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase">Delivery fee (GEL)</Label>
                <Input
                  type="number" min="0" step="0.01"
                  value={deliveryFee}
                  onChange={(e) => setDeliveryFee(e.target.value)}
                  className="h-9 rounded-lg tabular-nums"
                />
              </div>
              <div className="flex flex-col justify-end">
                <Label className="text-xs uppercase">Grand total</Label>
                <p className="text-2xl font-extrabold text-[#F25C05] tabular-nums" data-testid="quote-builder-grand-total">
                  {grandTotal.toFixed(2)} GEL
                </p>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase">Note to customer (optional)</Label>
              <Textarea
                rows={2}
                value={adminNote}
                onChange={(e) => setAdminNote(e.target.value)}
                className="rounded-xl"
                data-testid="quote-admin-note"
              />
            </div>

            {error && (
              <div className="rounded-xl border border-[#9b1c1c]/30 bg-[#9b1c1c]/8 px-3 py-2 text-sm text-[#9b1c1c]" data-testid="quote-builder-error">
                {error}
              </div>
            )}

            {result ? (
              <div className="rounded-2xl border border-[#A9B89A]/60 bg-[#A9B89A]/15 p-4 space-y-3 text-sm" data-testid="quote-builder-success">
                <p className="font-bold flex items-center gap-2 text-[#05223D]">
                  <CheckCircle2 className="h-4 w-4 text-[#0A4D8C]" />
                  Quote sent — total {result.total.toFixed(2)} GEL
                </p>
                <div className="flex flex-wrap gap-2">
                  <a
                    href={result.whatsapp_url} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1.5 rounded-lg bg-[#25D366] px-3 py-1.5 text-white font-bold"
                    data-testid="quote-builder-wa-link"
                  >
                    <Send className="h-3.5 w-3.5" /> Send on WhatsApp
                  </a>
                  <Button
                    variant="outline" size="sm" className="rounded-lg"
                    onClick={() => copyToClipboard(result.quote_url, 'url')}
                    data-testid="quote-builder-copy-url"
                  >
                    <ClipboardCopy className="h-3.5 w-3.5 mr-1.5" />
                    {copied === 'url' ? 'Copied!' : 'Copy quote URL'}
                  </Button>
                  <a
                    href={result.quote_url} target="_blank" rel="noreferrer"
                    className="text-xs text-[#0A4D8C] underline break-all"
                  >
                    {result.quote_url}
                  </a>
                </div>
              </div>
            ) : (
              <Button
                onClick={sendQuote}
                disabled={sending || subtotal === 0}
                className="w-full rounded-xl bg-[#F25C05] hover:bg-[#d44e02]"
                data-testid="quote-builder-send"
              >
                {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                Send quote
              </Button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
